import { useCallback, useEffect, useMemo, useRef } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isImageCreateData,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	getRemixMetadata,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { duplicateMauiApp } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { getActiveRemixCreateId } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import {
	beginRemixInsertRenderVerification,
	REMIX_DIAGRAM_RENDER_TIMEOUT_MS,
	releaseRemixInsertRenderOutcome,
	retainRemixInsertRenderOutcome,
	type RemixInsertRenderIdentity,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-insert-render-experience';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	RovoModifiedObject,
	TrackAIEventPayloads,
	TrackAIActioned,
	RemixAIResultActionedMetadata,
} from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { MessageActionPayload } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useChatStoreActions } from '@atlassian/conversation-assistant/controllers/chat-store';
import {
	countFragmentWords,
	getTopLevelNodeTypes,
} from '@atlassian/conversation-assistant/utils/content-features';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

// eslint-disable-next-line import/extensions -- `.utils` is part of the filename, not a file extension
import { getRemixResultPayload } from '../../state/machine/deriveRemixPhase.utils';
import { useEditorRemixContext } from './context';
import { useEditorRemixLifecycle, useEditorRemixLifecycleOwner } from './editorRemixLifecycleOwner';
import { getUnsupportedRemixNodeTypes } from './getUnsupportedRemixNodeTypes';
import { resolveFinalizeParentId } from './resolveFinalizeParentId';
import type {
	EditorRemixLifecycleEndReason,
	WhiteboardEmbedReplacementOutcome,
	WhiteboardEmbedReplacementResult,
} from './types';
import {
	appendConversationContextToUrl,
	awaitEditorInsertionAcknowledgement,
	EditorInsertionAcknowledgementTimeoutError,
	getRemixIdFromMessage,
	getRemixUrl,
	getMauiUrl,
	getStreamingWhiteboardEmbedUrl,
	getWhiteboardUrl,
	getWhiteboardCreateActionData,
	insertEmbedIntoPage,
	sendWhiteboardStreamCompleteCommand,
	needsReparenting,
	getReparentContentType,
	FINALIZABLE_CONTENT_ACTION_KEYS,
	fetchInfographicUrl,
	isPageLevelRemix,
} from './utils/message';
import { getMauiIdFromUrl } from './utils/remix-ids';

const isCancellationEndReason = (reason: EditorRemixLifecycleEndReason | undefined): boolean =>
	reason === 'aborted' || reason === 'cancelled';

const isFinalizePermissionError = (error: Error): boolean =>
	(error as Error & { errorAttributes?: { statusCode?: number } }).errorAttributes?.statusCode ===
	403;

const isAcknowledgementTimeout = (
	error: unknown,
): error is EditorInsertionAcknowledgementTimeoutError<{ inserted?: boolean }> =>
	error instanceof EditorInsertionAcknowledgementTimeoutError;

type WhiteboardReplacementError = Error & {
	whiteboardReplacementOutcome: WhiteboardEmbedReplacementOutcome;
};

const createWhiteboardReplacementError = (
	message: string,
	outcome: WhiteboardEmbedReplacementOutcome,
): WhiteboardReplacementError =>
	Object.assign(new Error(message), { whiteboardReplacementOutcome: outcome });

const releaseEarlyWhiteboardRenderOutcome = (localId: string | undefined): void => {
	if (localId) {
		releaseRemixInsertRenderOutcome({ type: 'localId', value: localId });
	}
};

type LifecycleOperationalAttributes = {
	/** Kind of content being inserted; never a content or remix identifier. */
	contentType: 'embed' | 'image';
	operation: 'insertionStart';
	outcome: 'rejected';
};

export function useOnComplete(): void {
	const retainedLifecycle = useEditorRemixLifecycleOwner()?.active;
	const { hasMachineAuthority } = useEditorRemixLifecycle();
	const {
		isViewMode,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		dynamicUiType,
		dynamicUiSubtype: dynamicUiSubtypeFromContext,
		dynamicUiSource,
		productConfig,
		conversationId,
		streamingAttemptId,
		interactionSource,
		aspectRatio,
		finalMessage,
		contentClassification,
		clearThinkingDecoration,
		onBeforeEditorRemixResultInsertion,
		replaceWhiteboardEmbed,
		setMauiEmbedFinalizeStatus,
		setThinkingComplete,
		finishRemixThinking,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onFinalizeSuccess,
		onFinalizeFailed,
		registerRemixResultTracking,
		onVariantUpdateComplete,
		onSubtypeMismatch,
		smartCreationConfig = { siteId: '', product: '', userId: '' },
		smartCreationContext,
		resetContext,
		editorRemixLifecycleAdapter,
		isRemixInsertingRef,
		pendingRemixInsertFlowIdRef,
	} = useEditorRemixContext();
	const streamGroupId =
		contentClassification?.type === 'whiteboard' ? contentClassification.streamGroupId : undefined;
	const featureArea = getRemixFeatureArea({ remixType: dynamicUiType });
	const featureAreaAttributes = useMemo(
		() => ({ ...(featureArea !== undefined && { featureArea }) }),
		[featureArea],
	);
	const experienceTracker = useAIMateExperienceTracker();
	const startRemixInsertRenderVerification = useCallback(
		(
			identity?: RemixInsertRenderIdentity,
			additionalAttributes?: Record<string, unknown>,
			reportingAttributes?: Record<string, unknown>,
			renderTimeoutMs?: number,
		): void => {
			const attributes = {
				...featureAreaAttributes,
				...additionalAttributes,
				...(reportingAttributes ?? { remixCreateId: getActiveRemixCreateId() }),
			};
			if (identity) {
				beginRemixInsertRenderVerification({
					attributes,
					experienceTracker,
					identity,
					renderTimeoutMs,
				});
			} else {
				experienceTracker.fail({
					name: ExperienceName.REMIX_INSERT,
					error: new Error(
						'render_identity_unavailable: No ID was present for the inserted Remix content',
					),
					attributes: { ...attributes, renderFailureReason: 'render_identity_unavailable' },
				});
			}
		},
		[experienceTracker, featureAreaAttributes],
	);
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	// Shadow lifecycle diagnostics carry no identifiers and must never interrupt insertion.
	const logLifecycleTelemetryBestEffort = useCallback(
		(attributes: LifecycleOperationalAttributes): void => {
			try {
				sendAnalyticsEvent({
					eventType: 'operational',
					actionSubject: 'editorRemixLifecycleTransition',
					action: 'failed',
					actionSubjectId: attributes.operation,
					attributes,
				});
			} catch {
				// Telemetry must never interrupt insertion.
			}
		},
		[sendAnalyticsEvent],
	);
	const { resolveConversationAction } = useChatStoreActions();

	const { userId: clientId, siteId } = smartCreationConfig;
	const action = finalMessage?.content?.message?.message?.actions?.[0];
	const actionData = action?.data;
	const actionKey = action?.key;
	const isCancelled = useCallback(
		() =>
			(getReparentContentType(actionKey) === 'whiteboard' ||
				contentClassification?.type === 'whiteboard') &&
			isCancellationEndReason(retainedLifecycle?.getEndReason()),
		[actionKey, contentClassification?.type, retainedLifecycle],
	);
	const remixId = getRemixIdFromMessage({ message: finalMessage });
	const agentMessageId = finalMessage?.content?.message?.message?.id;
	const mediaFileId = isImageCreateData(actionData) ? actionData.mediaFileId : undefined;
	const altText = isImageCreateData(actionData) ? actionData.altText : undefined;
	const backendDynamicUiSubtype = (actionData as { dynamicUiSubtype?: string } | undefined)
		?.dynamicUiSubtype;
	const isDiagramUpdate = isUpdate && needsReparenting(actionKey);
	const succeedDiagramRegenerate = useCallback(() => {
		if (!isDiagramUpdate) {
			return;
		}
		experienceTracker.succeed({
			name: ExperienceName.REMIX_REGENERATE,
			attributes: featureAreaAttributes,
		});
	}, [experienceTracker, featureAreaAttributes, isDiagramUpdate]);
	const failDiagramRegenerate = useCallback(
		(error: Error) => {
			if (!isDiagramUpdate) {
				return;
			}
			experienceTracker.fail({
				teamName: 'CC CE MAU',
				name: ExperienceName.REMIX_REGENERATE,
				error,
				attributes: featureAreaAttributes,
			});
		},
		[experienceTracker, featureAreaAttributes, isDiagramUpdate],
	);
	// Flag a true override only: both subtypes present and different. Excludes the
	// "Pick for me" case (no user selection).
	const isSubtypeMismatch =
		!!dynamicUiSubtypeFromContext &&
		!!backendDynamicUiSubtype &&
		dynamicUiSubtypeFromContext !== backendDynamicUiSubtype;
	const isLiveDoc = smartCreationContext?.confluence?.content?.subType === 'live';
	const contentType = smartCreationContext?.confluence?.content?.type;
	const currentPageId = smartCreationContext?.confluence?.content?.id;
	const spaceId = smartCreationContext?.confluence?.space?.id;
	const spaceKey = smartCreationContext?.confluence?.space?.key;
	const remixUrl = getRemixUrl(remixId, actionKey, spaceId, spaceKey);
	const finalWhiteboardCreateActionData = getWhiteboardCreateActionData(finalMessage);
	// For non-Confluence products, `context.editor` provides a pre-formed containerAri
	// for the duplicate remix API call and an editorId for routing the insert command
	// to the correct editor instance.
	const smartCreationEditorContext = smartCreationContext?.editor;
	const { selectedContentAnatomy, selectedContentWordCount, unsupportedRemixNodeTypes } =
		useMemo(() => {
			if (!dynamicUiSource) {
				return {
					selectedContentAnatomy: undefined,
					selectedContentWordCount: undefined,
					unsupportedRemixNodeTypes: undefined,
				};
			}

			return {
				selectedContentAnatomy: getTopLevelNodeTypes(
					dynamicUiSource.fullAdf,
					dynamicUiSource.startLocalId,
					dynamicUiSource.endLocalId,
				),
				selectedContentWordCount: countFragmentWords(dynamicUiSource.fragmentAdf ?? []),
				unsupportedRemixNodeTypes: getUnsupportedRemixNodeTypes(
					dynamicUiSource,
					productConfig?.isNodeUnsupportedForRemix,
				),
			};
		}, [dynamicUiSource, productConfig?.isNodeUnsupportedForRemix]);
	// The page/editor ID used to route EditorInsertContentCommand via the rovo content bridge.
	// Prefers the Confluence content ID; falls back to the editor UUID for non-Confluence products.
	const insertReceiverId = currentPageId ?? smartCreationEditorContext?.editorId;
	const shouldIncludeCwrAnalyticsUpdates = fg('cwr_ai_result_analytics_updates');
	const createRemixAdoptedEvent = useCallback(
		(
			objectId: string | undefined,
			objectType: RovoModifiedObject['objectType'],
		): TrackAIEventPayloads | undefined => {
			if (!actionKey || !objectId) {
				return;
			}

			return {
				type: 'adopted',
				actionKey,
				actionResult: 'contentFinalized',
				invokedFrom: 'remix',
				messageActions: [actionKey],
				proactive: false,
				rovoModifiedObjects: [{ objectType, objectId }],
				singleInstrumentationID: agentMessageId,
			};
		},
		[actionKey, agentMessageId],
	);
	const mauiEmbedActionedEvent = useMemo<TrackAIActioned & RemixAIResultActionedMetadata>(
		() => ({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			...(shouldIncludeCwrAnalyticsUpdates && actionKey ? { messageActions: [actionKey] } : {}),
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: getReparentContentType(actionKey),
				createdContentMetadata: {
					remixId,
					contentId: currentPageId,
					spaceId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
				userAction: 'insert',
				...(shouldIncludeCwrAnalyticsUpdates && actionKey
					? { userConfirmedActions: [{ actionKey, objectId: remixId }] }
					: {}),
			},
		}),
		[
			actionKey,
			agentMessageId,
			backendDynamicUiSubtype,
			currentPageId,
			dynamicUiSubtypeFromContext,
			dynamicUiType,
			isLiveDoc,
			remixId,
			shouldIncludeCwrAnalyticsUpdates,
			spaceId,
		],
	);
	const trackMauiEmbedActioned = useCallback(() => {
		trackAIMAUEvent(mauiEmbedActionedEvent);
	}, [mauiEmbedActionedEvent, trackAIMAUEvent]);
	const infographicActionedEvent = useMemo<TrackAIActioned & RemixAIResultActionedMetadata>(
		() => ({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			...(shouldIncludeCwrAnalyticsUpdates && actionKey ? { messageActions: [actionKey] } : {}),
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: 'image',
				createdContentMetadata: {
					mediaFileId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
				userAction: 'insert',
				...(shouldIncludeCwrAnalyticsUpdates && actionKey
					? { userConfirmedActions: [{ actionKey, objectId: mediaFileId }] }
					: {}),
			},
		}),
		[
			actionKey,
			agentMessageId,
			backendDynamicUiSubtype,
			dynamicUiSubtypeFromContext,
			dynamicUiType,
			isLiveDoc,
			mediaFileId,
			shouldIncludeCwrAnalyticsUpdates,
		],
	);
	const trackInfographicActioned = useCallback(() => {
		trackAIMAUEvent(infographicActionedEvent);
	}, [infographicActionedEvent, trackAIMAUEvent]);
	const completeRemixGeneratePreview = useCallback(() => {
		if (isUpdate) {
			return;
		}
		remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.PREVIEW_VISIBLE);
		remixGeneratePreviewExperience.success();
	}, [isUpdate]);
	const failRemixGeneratePreview = useCallback(
		(error: Error) => {
			if (isUpdate) {
				return;
			}
			remixGeneratePreviewExperience.failure({ metadata: { reason: error.message } });
		},
		[isUpdate],
	);

	/**
	 * Calls the convo-ai action endpoint when `aifc_finalize_convo_ai` is on.
	 * `onError` re-throws so callers can detect failure — without it, the chat
	 * store's safe-action wrapper would silently swallow the error.
	 */
	const finalizeAction = useCallback(
		async ({
			contentId,
			parentId,
			resolvedSpaceId,
		}: {
			contentId: string;
			parentId: string | undefined;
			resolvedSpaceId: string | undefined;
		}): Promise<void> => {
			if (!conversationId) {
				return;
			}

			// Only finalize via convo-ai for Confluence object types (pages, whiteboards,
			// databases). MAUI apps and images don't need this endpoint.
			if (!actionKey || !FINALIZABLE_CONTENT_ACTION_KEYS.has(actionKey)) {
				return;
			}

			const message = finalMessage?.content?.message?.message;
			const messageAction = message?.actions?.[0];
			if (!message || !messageAction) {
				return;
			}

			const isActionComplete =
				finalMessage?.content.streamingComplete ||
				messageAction?.status?.toLowerCase() === 'planned' ||
				messageAction?.status?.toLowerCase() === 'finished';

			if (!isActionComplete) {
				return;
			}

			const existingData = (messageAction.data as Record<string, unknown>) ?? {};
			const existingMetadata = (existingData.metadata as Record<string, unknown>) ?? {};

			const payload: MessageActionPayload = {
				context: {
					key: messageAction.key,
					invocationId: messageAction.invocationId,
					actionId: messageAction.actionId,
					displayName: messageAction.displayName,
					status: messageAction.status,
					toolSource: messageAction.toolSource,
					humanMessageId: messageAction.humanMessageId,
					confirmationMessage: messageAction.confirmationMessage,
					executedData: messageAction.executedData,
					messageId: message.id,
					resolution: {
						type: 'confirm',
						message: 'finalize',
					},
					data: {
						...existingData,
						metadata: {
							...existingMetadata,
							contentId,
							parentId,
							spaceId: resolvedSpaceId,
						},
					},
				},
			};

			await resolveConversationAction({
				conversationId,
				messageId: message.id,
				payload,
				actionContentProcessor: (msg) => ({
					message: { message_metadata: msg?.message_metadata },
				}),
				onError: ({ error }) => {
					throw error;
				},
			});
		},
		[finalMessage, resolveConversationAction, conversationId, actionKey],
	);

	const clearThinkingDecorationRef = useRef(clearThinkingDecoration);
	useEffect(() => {
		clearThinkingDecorationRef.current = clearThinkingDecoration;
	}, [clearThinkingDecoration]);

	const replaceWhiteboardEmbedRef = useRef(replaceWhiteboardEmbed);
	useEffect(() => {
		replaceWhiteboardEmbedRef.current = replaceWhiteboardEmbed;
	}, [replaceWhiteboardEmbed]);

	const setMauiEmbedFinalizeStatusRef = useRef(setMauiEmbedFinalizeStatus);
	useEffect(() => {
		setMauiEmbedFinalizeStatusRef.current = setMauiEmbedFinalizeStatus;
	}, [setMauiEmbedFinalizeStatus]);

	// Captures the suppressed contentId so the abandoned-stream backstop can
	// release it when finalMessage (hence remixId) is null and no other id source exists.
	const streamingContentIdRef = useRef<string | undefined>(undefined);

	const setThinkingCompleteRef = useRef(setThinkingComplete);
	useEffect(() => {
		setThinkingCompleteRef.current = setThinkingComplete;
	}, [setThinkingComplete]);

	const finishRemixThinkingRef = useRef(finishRemixThinking);
	useEffect(() => {
		finishRemixThinkingRef.current = finishRemixThinking;
	}, [finishRemixThinking]);

	const hasInsertedRef = useRef(false);
	const shouldSettleRetainedLifecycleRef = useRef(true);
	const hasEarlyInsertedWhiteboardRef = useRef(false);
	const earlyWhiteboardInsertionAcknowledgementRef = useRef<Promise<boolean> | undefined>(
		undefined,
	);
	const hasDeferredWhiteboardRecoveryRef = useRef(false);
	const earlyInsertedWhiteboardAtRef = useRef<number | undefined>(undefined);
	const earlyInsertedWhiteboardLocalIdRef = useRef<string | undefined>(undefined);
	const earlyInsertedWhiteboardUrlRef = useRef<string | undefined>(undefined);

	const resetStreamingWhiteboardState = useCallback(() => {
		releaseEarlyWhiteboardRenderOutcome(earlyInsertedWhiteboardLocalIdRef.current);
		hasInsertedRef.current = false;
		hasEarlyInsertedWhiteboardRef.current = false;
		earlyInsertedWhiteboardAtRef.current = undefined;
		earlyInsertedWhiteboardLocalIdRef.current = undefined;
		earlyInsertedWhiteboardUrlRef.current = undefined;
		if (streamingContentIdRef.current) {
			setMauiEmbedFinalizeStatusRef.current?.({
				contentId: streamingContentIdRef.current,
				status: 'needs-finalize',
			});
			streamingContentIdRef.current = undefined;
		}
	}, []);

	const previousConversationIdRef = useRef(conversationId);
	useEffect(() => {
		if (previousConversationIdRef.current === conversationId) {
			return;
		}
		previousConversationIdRef.current = conversationId;
		resetStreamingWhiteboardState();
	}, [conversationId, resetStreamingWhiteboardState]);

	const previousStreamingAttemptIdRef = useRef(streamingAttemptId);
	useEffect(() => {
		if (previousStreamingAttemptIdRef.current === streamingAttemptId) {
			return;
		}
		previousStreamingAttemptIdRef.current = streamingAttemptId;
		if (streamingAttemptId) {
			resetStreamingWhiteboardState();
		}
	}, [resetStreamingWhiteboardState, streamingAttemptId]);

	const insertEarlyStreamingWhiteboard = useCallback(
		(contentId: string) => {
			const localId = crypto.randomUUID();
			const earlyRemixUrl = getStreamingWhiteboardEmbedUrl(
				contentId,
				streamGroupId ?? streamingAttemptId ?? localId,
			);
			retainRemixInsertRenderOutcome({ type: 'localId', value: localId });
			hasEarlyInsertedWhiteboardRef.current = true;
			earlyInsertedWhiteboardAtRef.current = Date.now();
			earlyInsertedWhiteboardLocalIdRef.current = localId;
			earlyInsertedWhiteboardUrlRef.current = earlyRemixUrl;
			streamingContentIdRef.current = contentId;

			// Suppress before insert so the banner mounts with a known status instead of warning mid-stream.
			setMauiEmbedFinalizeStatusRef.current?.({ contentId, status: 'suppressed' });

			const acknowledgement = insertEmbedIntoPage({
				url: earlyRemixUrl,
				insertReceiverId,
				isUpdate,
				localId,
				...(retainedLifecycle ? { requireAcknowledgement: true, cancelOnTimeout: true } : {}),
			});
			if (retainedLifecycle) {
				retainedLifecycle.transition({ type: 'DIRECT_INSERTION_STARTED' });
				earlyWhiteboardInsertionAcknowledgementRef.current = acknowledgement
					? acknowledgement.then((result) => result?.inserted === true).catch(() => false)
					: Promise.resolve(false);
			}

			// The whiteboard iframe is now the streaming surface. Hide only the editor-owned
			// generating decoration; keep the Remix flow mounted so finalMessage can still
			// trigger finalize/reparent and history recording.
			clearThinkingDecorationRef.current?.();
		},
		[insertReceiverId, isUpdate, retainedLifecycle, streamGroupId, streamingAttemptId],
	);

	const replaceEarlyStreamingWhiteboard = useCallback(
		(contentId: string, finalizedUrl: string): WhiteboardEmbedReplacementResult => {
			const result = replaceWhiteboardEmbedRef.current?.({
				contentId,
				finalizedUrl,
				localId: earlyInsertedWhiteboardLocalIdRef.current,
			});

			if (typeof result === 'object') {
				return result;
			}
			return result === true
				? { acknowledged: true, outcome: 'replaced' }
				: { acknowledged: false, outcome: 'unknown' };
		},
		[],
	);

	const handleInsertMauiEmbedIntoPage = useCallback(
		async (isCancelled: () => boolean) => {
			let url = '';
			let acknowledgementTimedOut = false;

			if (isCancelled()) {
				return;
			}

			if (!conversationId || !remixId || !remixUrl) {
				return;
			}
			if (
				retainedLifecycle &&
				hasEarlyInsertedWhiteboardRef.current &&
				!(await earlyWhiteboardInsertionAcknowledgementRef.current)
			) {
				if (isCancelled()) {
					return;
				}
				streamingContentIdRef.current = undefined;
				retainedLifecycle.transition({ type: 'INSERTION_FAILED' });
				return;
			}
			if (isCancelled()) {
				return;
			}
			const shouldDelayActionedEvent = isExperimentEnabled(
				'platform_editor_maui_delay_actioned_event',
			);
			// MaUI apps only: infographics are tracked elsewhere; whiteboards, slides, and databases are excluded.
			const shouldTrackRemixRetention =
				actionKey === DYNAMIC_UI_CREATE_KEY &&
				isExperimentEnabled('platform_maui_retention_analytics');
			const insertionLocalId = earlyInsertedWhiteboardLocalIdRef.current ?? crypto.randomUUID();
			let insertedContentLocalId = hasEarlyInsertedWhiteboardRef.current
				? earlyInsertedWhiteboardLocalIdRef.current
				: undefined;
			let adoptedEvent: TrackAIEventPayloads | undefined;
			const insertRemixEmbed = async (embedUrl: string) => {
				let result: Awaited<ReturnType<typeof insertEmbedIntoPage>>;
				try {
					result = await insertEmbedIntoPage({
						url: embedUrl,
						insertReceiverId,
						isUpdate,
						localId: insertionLocalId,
						requireAcknowledgement: true,
					});
				} catch (error) {
					if (!isAcknowledgementTimeout(error)) {
						throw error;
					}
					acknowledgementTimedOut = true;
					if (retainedLifecycle) {
						shouldSettleRetainedLifecycleRef.current = false;
						retainedLifecycle.end('abandoned');
					}
					sendAnalyticsEvent({
						eventType: 'operational',
						actionSubject: 'editorInsertionAcknowledgement',
						action: 'failed',
						actionSubjectId: 'timeout',
						attributes: { contentType: 'embed', reason: 'acknowledgement-timeout' },
					});
					void error.lateResult
						.then((result) =>
							sendAnalyticsEvent({
								eventType: 'operational',
								actionSubject: 'editorInsertionAcknowledgement',
								action: 'settled',
								attributes: {
									contentType: 'embed',
									outcome: result?.inserted === true ? 'insertedLate' : 'notInserted',
								},
							}),
						)
						.catch(() => undefined);
				}
				if (!acknowledgementTimedOut && result?.inserted !== true) {
					throw new Error('Editor insertion was not acknowledged');
				}
				insertedContentLocalId = insertionLocalId;
			};

			const insertionOwnerFlowId =
				getReparentContentType(actionKey) === 'whiteboard' ? retainedLifecycle?.remixId : undefined;
			if (insertionOwnerFlowId) {
				pendingRemixInsertFlowIdRef.current = insertionOwnerFlowId;
			}

			experienceTracker.start({
				name: ExperienceName.REMIX_INSERT,
				id: ExperienceName.REMIX_INSERT,
				attributes: {
					...featureAreaAttributes,
					actionKey,
					dynamicUiType,
					contentType: getReparentContentType(actionKey),
					conversationChannelId: conversationId,
					remixCreateId: getActiveRemixCreateId(),
				},
			});

			if (!shouldDelayActionedEvent) {
				trackMauiEmbedActioned();
			}

			try {
				// For MAUI apps, duplicate the app first, then insert the duplicated URL.
				// When context.editor is set (gated by platform_editor_maui_jira_updates), use its
				// pre-formed containerAri. Otherwise build a Confluence ARI from the page ID.
				if (actionKey === DYNAMIC_UI_CREATE_KEY && (currentPageId || smartCreationEditorContext)) {
					const duplicateExperienceAttributes = {
						...featureAreaAttributes,
						appId: remixId,
						currentPageId,
						source: 'mauiEmbedFinalizeButtons',
					};
					experienceTracker.start({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						id: ExperienceName.REMIX_DUPLICATE_APP,
						attributes: duplicateExperienceAttributes,
					});

					let duplicatedAppId: string | undefined;
					try {
						const { id: newAppId } = await duplicateMauiApp(
							remixId,
							smartCreationEditorContext
								? { containerAri: smartCreationEditorContext.containerAri }
								: {
										containerContentId: currentPageId ?? '',
										cloudId: smartCreationConfig.siteId,
										contentType,
									},
						);
						duplicatedAppId = newAppId;
					} catch (error) {
						sendAnalyticsEvent({
							eventType: 'operational',
							actionSubject: 'mauiAppDuplicate',
							action: 'failed',
							actionSubjectId: 'mauiEmbedInsertIntoPage',
							attributes: {
								originalId: remixId,
							},
						});

						const duplicateError =
							error instanceof Error ? error : new Error('Failed to duplicate MAUI app');
						experienceTracker.fail({
							teamName: 'CC CE MAU',
							name: ExperienceName.REMIX_DUPLICATE_APP,
							error: duplicateError,
							attributes: duplicateExperienceAttributes,
						});
						experienceTracker.fail({
							teamName: 'CC CE MAU',
							name: ExperienceName.REMIX_INSERT,
							error: duplicateError,
							attributes: {
								...duplicateExperienceAttributes,
								failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
							},
						});

						// Duplication failed — insert the original URL as a fallback
						// so the user isn't left with nothing.
						await insertRemixEmbed(remixUrl);
						url = remixUrl;
						completeRemixGeneratePreview();
					}

					if (duplicatedAppId) {
						const newAppId = duplicatedAppId;
						sendAnalyticsEvent({
							eventType: 'operational',
							actionSubject: 'mauiAppDuplicate',
							action: 'succeeded',
							actionSubjectId: 'mauiEmbedInsertIntoPage',
							attributes: {
								originalId: remixId,
								newId: newAppId,
							},
						});

						experienceTracker.succeed({
							name: ExperienceName.REMIX_DUPLICATE_APP,
							attributes: {
								...duplicateExperienceAttributes,
								newAppId,
								remixCreateId: getActiveRemixCreateId(),
							},
						});
						const duplicatedUrl = getMauiUrl(duplicatedAppId);
						await insertRemixEmbed(duplicatedUrl);
						url = duplicatedUrl;
						startRemixInsertRenderVerification({ type: 'localId', value: insertionLocalId });
						completeRemixGeneratePreview();
						adoptedEvent = shouldIncludeCwrAnalyticsUpdates
							? createRemixAdoptedEvent(duplicatedAppId, 'maui-app')
							: undefined;
					}
					return { acknowledgementTimedOut, fieldId: undefined, url };
				}

				if (!needsReparenting(actionKey)) {
					// Fallback for MAUI apps reached without a currentPageId (the
					// duplicate flow above requires one). Insert the original URL
					// as-is.
					await insertRemixEmbed(remixUrl);
					url = remixUrl;
					try {
						await finalizeAction({
							contentId: remixId,
							parentId: currentPageId,
							resolvedSpaceId: spaceId,
						});
					} catch (error) {
						const finalizeError =
							error instanceof Error ? error : new Error('finalizeAction failed');
						experienceTracker.fail({
							teamName: 'CC CE MAU',
							name: ExperienceName.REMIX_INSERT,
							error: finalizeError,
							attributes: { ...featureAreaAttributes, remixCreateId: getActiveRemixCreateId() },
						});
						failRemixGeneratePreview(finalizeError);
						return;
					}
					startRemixInsertRenderVerification({ type: 'localId', value: insertionLocalId });
					adoptedEvent = shouldIncludeCwrAnalyticsUpdates
						? createRemixAdoptedEvent(remixId, 'maui-app')
						: undefined;
					completeRemixGeneratePreview();
					return { acknowledgementTimedOut, fieldId: undefined, url };
				}

				// Whiteboards must be durable before the page keeps a final embed URL. If we
				// inserted a temporary streaming URL on the first chunk, finalization still runs
				// here and then swaps that exact node to the canonical finalized URL by localId.
				//
				// Append `_rc` so the Confluence in-embed recovery picker can
				// finalize a stuck embed via the convo-ai endpoint if inline
				// finalize fails (e.g. permission error on current space).
				// Only when the killswitch is OFF; falls back to plain remixUrl.
				const finalizeAnywhereKillswitchOn =
					// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
					FeatureGates.getExperimentValue<boolean>(
						'maui-native-embed-finalize-diagram-anywhere',
						'value',
						false,
					);
				// Recovery URL used only when finalize fails. The board was NOT moved
				// server-side (it still lives in the placeholder Rovo space), so the
				// embed must point at a placeholder-space URL — NOT `remixUrl`, which
				// embeds the current space key and would make the unfinalized diagram
				// look as if it had been moved. We deliberately omit `spaceKey` here so
				// `getWhiteboardUrl` falls back to the `~{spaceId}`/`~` placeholder, and
				// append `_rc` so the in-embed recovery picker can finalize it later.
				// Only when the killswitch is OFF; otherwise the embed is left as-is.
				const placeholderWhiteboardUrl = getWhiteboardUrl(remixId, spaceId);
				const whiteboardUrlWithRc = !finalizeAnywhereKillswitchOn
					? appendConversationContextToUrl(placeholderWhiteboardUrl, {
							conversationId,
							messageId: agentMessageId,
							invocationId: action?.invocationId,
						})
					: placeholderWhiteboardUrl;

				let finalizeError: Error | undefined;
				try {
					const parentIdForFinalize = await resolveFinalizeParentId(currentPageId);
					if (isCancelled()) {
						return;
					}
					await finalizeAction({
						contentId: remixId,
						parentId: parentIdForFinalize,
						resolvedSpaceId: spaceId,
					});
					if (isCancelled()) {
						return;
					}
					onFinalizeSuccess?.({
						contentId: remixId,
						parentId: parentIdForFinalize,
					});
				} catch (error) {
					if (isCancelled()) {
						return;
					}
					finalizeError =
						error instanceof Error ? error : new Error('Failed to finalize whiteboard');
				}

				if (!finalizeError) {
					// Finalize succeeded — insert/replace with plain URL (no `_rc` needed).
					if (hasEarlyInsertedWhiteboardRef.current) {
						// Success rewrites the URL → FINALIZED, keeping the banner hidden. Only a retained
						// lifecycle requires the replacement to be positively acknowledged.
						const replacement = replaceEarlyStreamingWhiteboard(remixId, remixUrl);
						if (retainedLifecycle && !replacement.acknowledged) {
							throw createWhiteboardReplacementError(
								'Finalized whiteboard replacement was not acknowledged',
								replacement.outcome,
							);
						}
						streamingContentIdRef.current = undefined;
					} else {
						await insertRemixEmbed(remixUrl);
						if (isCancelled()) {
							return;
						}
					}
					url = remixUrl;
					startRemixInsertRenderVerification(
						{ type: 'localId', value: insertionLocalId },
						undefined,
						undefined,
						REMIX_DIAGRAM_RENDER_TIMEOUT_MS,
					);
					const reparentedContentType = getReparentContentType(actionKey);
					if (shouldIncludeCwrAnalyticsUpdates && reparentedContentType) {
						adoptedEvent = createRemixAdoptedEvent(remixId, reparentedContentType);
					}
					completeRemixGeneratePreview();
					succeedDiagramRegenerate();
					return { acknowledgementTimedOut, fieldId: undefined, url };
				}

				failDiagramRegenerate(finalizeError);

				if (hasEarlyInsertedWhiteboardRef.current) {
					// Finalize failed — swap streaming URL for the placeholder `_rc`
					// URL so the in-embed recovery picker can finalize into a writable
					// space, regardless of error type (permission or otherwise).
					// When the killswitch is ON we skip the swap and leave the stuck
					// streaming embed in place (no `_rc` recovery flow).
					if (!finalizeAnywhereKillswitchOn) {
						const replacement = replaceEarlyStreamingWhiteboard(remixId, whiteboardUrlWithRc);
						if (retainedLifecycle && !replacement.acknowledged) {
							throw createWhiteboardReplacementError(
								'Recoverable whiteboard replacement was not acknowledged',
								replacement.outcome,
							);
						}
						earlyInsertedWhiteboardUrlRef.current = whiteboardUrlWithRc;
					}
					if (isFinalizePermissionError(finalizeError)) {
						startRemixInsertRenderVerification(
							{ type: 'localId', value: insertionLocalId },
							{ isFinalizePermissionError: true },
							undefined,
							REMIX_DIAGRAM_RENDER_TIMEOUT_MS,
						);
						completeRemixGeneratePreview();
					} else {
						experienceTracker.fail({
							teamName: 'CC CE MAU',
							name: ExperienceName.REMIX_INSERT,
							error: finalizeError,
							attributes: { remixCreateId: getActiveRemixCreateId() },
						});
						failRemixGeneratePreview(finalizeError);
					}
				} else {
					experienceTracker.fail({
						teamName: 'CC CE MAU',
						name: ExperienceName.REMIX_INSERT,
						error: finalizeError,
						attributes: { remixCreateId: getActiveRemixCreateId() },
					});
					failRemixGeneratePreview(finalizeError);
				}

				if (hasEarlyInsertedWhiteboardRef.current) {
					// The diagram remains inserted on the page. Hand the failure to the
					// product integration so it can open its recovery UX. For Confluence
					// this opens the shared MAUI embed space picker (the same UI used by
					// the paste / 2-minute needs-finalize banner flow), giving the user a
					// way to choose a space they can write to and finalize there.
					if (onFinalizeFailed) {
						// Hand the picker the URL that is actually on the embed node: the
						// placeholder-space URL carrying `_rc` (set above via
						// replaceEarlyStreamingWhiteboard). This lets the picker (1) parse
						// `_rc` and finalize via the convo-ai `/action` endpoint, and (2)
						// match the node in updateUrlInDoc so the success rewrite strips
						// `_rc` and swaps the space key. Passing `remixUrl` here would lack
						// `_rc` (forcing the monolith `/finalize` path) and would not match
						// the embed node.
						const recoveryWasDeferred = onFinalizeFailed({
							contentId: remixId,
							originalUrl: whiteboardUrlWithRc,
						});
						if (recoveryWasDeferred) {
							hasDeferredWhiteboardRecoveryRef.current = true;
							// The picker drives recovery from here; clear the streaming
							// suppression bookkeeping so the embed's banner state isn't left
							// stuck in a suppressed state.
							streamingContentIdRef.current = undefined;
						} else if (streamingContentIdRef.current) {
							setMauiEmbedFinalizeStatusRef.current?.({
								contentId: streamingContentIdRef.current,
								status: 'needs-finalize',
							});
							streamingContentIdRef.current = undefined;
							hasDeferredWhiteboardRecoveryRef.current = true;
						}
					} else if (streamingContentIdRef.current) {
						// No product recovery UX wired — fall back to the needs-finalize
						// banner (embed is still in a Rovo system space).
						setMauiEmbedFinalizeStatusRef.current?.({
							contentId: streamingContentIdRef.current,
							status: 'needs-finalize',
						});
						streamingContentIdRef.current = undefined;
						hasDeferredWhiteboardRecoveryRef.current = true;
					}
					return {
						acknowledgementTimedOut,
						fieldId: undefined,
						url: earlyInsertedWhiteboardUrlRef.current,
					};
				}
			} catch (error) {
				if (isCancelled()) {
					return;
				}
				const insertError =
					error instanceof Error ? error : new Error('Failed to insert into page');
				const whiteboardReplacementOutcome = (insertError as Partial<WhiteboardReplacementError>)
					.whiteboardReplacementOutcome;
				experienceTracker.fail({
					teamName: 'CC CE MAU',
					name: ExperienceName.REMIX_INSERT,
					error: insertError,
					attributes: {
						...featureAreaAttributes,
						...(whiteboardReplacementOutcome && { whiteboardReplacementOutcome }),
					},
				});
				failRemixGeneratePreview(insertError);
			} finally {
				if (!isCancelled()) {
					if (
						insertionOwnerFlowId &&
						pendingRemixInsertFlowIdRef.current === insertionOwnerFlowId
					) {
						pendingRemixInsertFlowIdRef.current = undefined;
					}
					if (!shouldDelayActionedEvent && adoptedEvent) {
						trackAIMAUEvent(adoptedEvent);
					}
					if (
						insertedContentLocalId &&
						insertReceiverId &&
						registerRemixResultTracking &&
						(shouldDelayActionedEvent || shouldTrackRemixRetention)
					) {
						registerRemixResultTracking({
							shouldDelayActionedEvent,
							operation: isUpdate ? 'edit' : 'create',
							previousResultId: getMauiIdFromUrl(historyEntryForUpdate?.url),
							adoptedAnalyticsEvent: adoptedEvent,
							analyticsEvent: mauiEmbedActionedEvent,
							identity: { attribute: 'localId', value: insertedContentLocalId },
							insertedAt: earlyInsertedWhiteboardAtRef.current,
							interactionSource,
							receiverId: insertReceiverId,
						});
					}
				}
			}
		},
		[
			action,
			conversationId,
			contentType,
			remixUrl,
			remixId,
			actionKey,
			onFinalizeSuccess,
			onFinalizeFailed,
			pendingRemixInsertFlowIdRef,
			spaceId,
			agentMessageId,
			smartCreationEditorContext,
			currentPageId,
			dynamicUiType,
			featureAreaAttributes,
			insertReceiverId,
			finalizeAction,
			completeRemixGeneratePreview,
			failRemixGeneratePreview,
			failDiagramRegenerate,
			succeedDiagramRegenerate,
			experienceTracker,
			startRemixInsertRenderVerification,
			trackMauiEmbedActioned,
			mauiEmbedActionedEvent,
			createRemixAdoptedEvent,
			shouldIncludeCwrAnalyticsUpdates,
			registerRemixResultTracking,
			historyEntryForUpdate?.url,
			interactionSource,
			sendAnalyticsEvent,
			trackAIMAUEvent,
			smartCreationConfig.siteId,
			isUpdate,
			replaceEarlyStreamingWhiteboard,
			retainedLifecycle,
		],
	);

	const handleInsertInfographicIntoPage = useCallback(async () => {
		if (!mediaFileId || !clientId || !insertReceiverId || !siteId) {
			return;
		}
		const shouldDelayActionedEvent = isExperimentEnabled(
			'platform_editor_maui_delay_actioned_event',
		);
		const adoptedEvent = shouldIncludeCwrAnalyticsUpdates
			? createRemixAdoptedEvent(mediaFileId, 'image')
			: undefined;
		const reportingAttributes = {
			...featureAreaAttributes,
			conversationChannelId: conversationId,
			remixCreateId: getActiveRemixCreateId(),
		};

		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT,
			id: ExperienceName.REMIX_INSERT,
			attributes: {
				actionKey,
				dynamicUiType,
				contentType: 'image',
				...reportingAttributes,
			},
		});

		if (!shouldDelayActionedEvent) {
			trackInfographicActioned();
		}

		try {
			const mediaUrl = await fetchInfographicUrl({
				mediaFileId,
				clientId,
				product: 'confluence',
			});

			const insertion = getBrowserBridgeAPI().send(EditorInsertContentCommand, {
				senderId: ROVO_CHAT_CLIENT_ID,
				receiverId: getConfluenceEditorClientId(insertReceiverId),
				requireInsertionAcknowledgement: true,
				mode: 'upload-and-insert-media',
				placement: isPageLevelRemix(dynamicUiSource)
					? 'end-of-doc'
					: isUpdate && fg('platform_editor_ai_remix_toolbar')
						? 'replace-selection'
						: 'after-block',
				useStoredSelection: true,
				conversationChannelId: conversationId,
				interactionSource,
				aspectRatio,
				// The insert command records the subtype the backend actually produced,
				// falling back to the user's pick only if the backend returned none.
				remixSubtype: backendDynamicUiSubtype ?? dynamicUiSubtypeFromContext,
				// The editor resolves and applies it (blank means no alt). On restyle
				// (`replace-selection`) it inserts a fresh node, so no stale alt carries over.
				...(altText !== undefined && { altText }),
				data: {
					type: 'image',
					url: mediaUrl,
					presentation: undefined,
				},
			});
			let insertionResult: Awaited<ReturnType<typeof insertion.result>>;
			let acknowledgementTimedOut = false;
			try {
				insertionResult = await awaitEditorInsertionAcknowledgement(insertion);
			} catch (error) {
				if (!retainedLifecycle || !isAcknowledgementTimeout(error)) {
					throw error;
				}
				acknowledgementTimedOut = true;
				shouldSettleRetainedLifecycleRef.current = false;
				retainedLifecycle.end('abandoned');
				sendAnalyticsEvent({
					eventType: 'operational',
					actionSubject: 'editorInsertionAcknowledgement',
					action: 'failed',
					actionSubjectId: 'timeout',
					attributes: { contentType: 'image', reason: 'acknowledgement-timeout' },
				});
				void error.lateResult
					.then((result) =>
						sendAnalyticsEvent({
							eventType: 'operational',
							actionSubject: 'editorInsertionAcknowledgement',
							action: 'settled',
							attributes: {
								contentType: 'image',
								outcome: result?.inserted === true ? 'insertedLate' : 'notInserted',
							},
						}),
					)
					.catch(() => undefined);
			}
			if (
				!acknowledgementTimedOut &&
				(insertionResult?.inserted !== true || !insertionResult.mediaFileId)
			) {
				throw new Error('Editor media insertion was not acknowledged');
			}
			if (shouldDelayActionedEvent || isExperimentEnabled('platform_maui_retention_analytics')) {
				const delayedResult = insertionResult
					? Promise.resolve(insertionResult)
					: insertion.result();
				void delayedResult
					.then((result) => {
						if (result?.mediaFileId && registerRemixResultTracking) {
							registerRemixResultTracking({
								shouldDelayActionedEvent,
								operation: isUpdate ? 'edit' : 'create',
								// `fieldId-placeholder` is the existing fallback for a missing media ID,
								// not a result that can be matched in the document.
								previousResultId:
									historyEntryForUpdate?.fieldId === 'fieldId-placeholder'
										? undefined
										: historyEntryForUpdate?.fieldId,
								sourceResultId: mediaFileId,
								adoptedAnalyticsEvent: adoptedEvent,
								analyticsEvent: infographicActionedEvent,
								identity: { attribute: 'id', value: result.mediaFileId },
								interactionSource,
								receiverId: insertReceiverId,
							});
						}
					})
					.catch(() => undefined);
			}

			const insertedMediaFileId = insertionResult?.mediaFileId;
			startRemixInsertRenderVerification(
				insertedMediaFileId ? { type: 'mediaId', value: insertedMediaFileId } : undefined,
				undefined,
				reportingAttributes,
			);
			if (!shouldDelayActionedEvent && adoptedEvent) {
				trackAIMAUEvent(adoptedEvent);
			}
			completeRemixGeneratePreview();
			return {
				acknowledgementTimedOut,
				fieldId: insertionResult?.mediaFileId ?? 'fieldId-placeholder',
				url: undefined,
			};
		} catch (error) {
			const insertError =
				error instanceof Error ? error : new Error('Failed to insert infographic into page');
			experienceTracker.fail({
				teamName: 'CC CE MAU',
				name: ExperienceName.REMIX_INSERT,
				error: insertError,
				attributes: reportingAttributes,
			});
			failRemixGeneratePreview(insertError);
			return;
		}
	}, [
		mediaFileId,
		altText,
		clientId,
		siteId,
		dynamicUiType,
		featureAreaAttributes,
		actionKey,
		experienceTracker,
		startRemixInsertRenderVerification,
		conversationId,
		dynamicUiSource,
		trackInfographicActioned,
		infographicActionedEvent,
		createRemixAdoptedEvent,
		shouldIncludeCwrAnalyticsUpdates,
		registerRemixResultTracking,
		insertReceiverId,
		dynamicUiSubtypeFromContext,
		backendDynamicUiSubtype,
		isUpdate,
		interactionSource,
		aspectRatio,
		completeRemixGeneratePreview,
		failRemixGeneratePreview,
		retainedLifecycle,
		historyEntryForUpdate?.fieldId,
		sendAnalyticsEvent,
		trackAIMAUEvent,
	]);

	const handleInsertMauiEmbedIntoPageRef = useRef(handleInsertMauiEmbedIntoPage);
	useEffect(() => {
		handleInsertMauiEmbedIntoPageRef.current = handleInsertMauiEmbedIntoPage;
	}, [handleInsertMauiEmbedIntoPage]);

	const handleInsertInfographicIntoPageRef = useRef(handleInsertInfographicIntoPage);
	useEffect(() => {
		handleInsertInfographicIntoPageRef.current = handleInsertInfographicIntoPage;
	}, [handleInsertInfographicIntoPage]);

	// Clear the previous stream before an incoming classification can insert a new whiteboard.
	useEffect(() => {
		if (!finalMessage && fg('platform_editor_ai_remix_in_editor_loading')) {
			releaseEarlyWhiteboardRenderOutcome(earlyInsertedWhiteboardLocalIdRef.current);
			hasInsertedRef.current = false;
			isRemixInsertingRef.current = false;
			shouldSettleRetainedLifecycleRef.current = true;
			hasEarlyInsertedWhiteboardRef.current = false;
			earlyWhiteboardInsertionAcknowledgementRef.current = undefined;
			hasDeferredWhiteboardRecoveryRef.current = false;
			earlyInsertedWhiteboardAtRef.current = undefined;
			earlyInsertedWhiteboardLocalIdRef.current = undefined;
			earlyInsertedWhiteboardUrlRef.current = undefined;
			// Backstop for abandoned streams: finalMessage (hence remixId) is null here,
			// so the ref is the only id source. No-op if success/failure already settled.
			if (streamingContentIdRef.current) {
				setMauiEmbedFinalizeStatusRef.current?.({
					contentId: streamingContentIdRef.current,
					status: 'needs-finalize',
				});
				streamingContentIdRef.current = undefined;
			}
		}
	}, [finalMessage, isRemixInsertingRef]);

	useEffect(() => {
		const contentId =
			contentClassification?.type === 'whiteboard'
				? contentClassification.data?.contentId
				: undefined;

		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (isViewMode) {
			return;
		}
		if (hasEarlyInsertedWhiteboardRef.current) {
			return;
		}
		if (!contentId) {
			return;
		}
		if (hasMachineAuthority && !retainedLifecycle) {
			return;
		}
		if (isCancelled()) {
			return;
		}

		// Insert the temporary whiteboard shell when the first real ACTION_PART arrives.
		// This avoids showing an empty whiteboard during long gaps between classification
		// and the first streamed diagram content. The same localId is later used to replace
		// this temporary URL with the finalized canonical URL, even if native-embeds has
		// transformed the Smart Link into a native whiteboard extension in the meantime.
		if (retainedLifecycle) {
			onBeforeEditorRemixResultInsertion?.();
		}
		insertEarlyStreamingWhiteboard(contentId);
	}, [
		contentClassification,
		insertEarlyStreamingWhiteboard,
		isViewMode,
		onBeforeEditorRemixResultInsertion,
		retainedLifecycle,
		hasMachineAuthority,
		isCancelled,
	]);

	useEffect(() => {
		if (isViewMode) {
			return;
		}

		if (
			actionKey &&
			finalMessage &&
			!hasInsertedRef.current &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			if (hasMachineAuthority && !retainedLifecycle) {
				return;
			}
			const cancelledBeforeInsertion = isCancelled();
			hasInsertedRef.current = true;
			if (!cancelledBeforeInsertion) {
				// Insertion has no reactive signal and the preview has already closed by now, so the
				// lifecycle-phase projection reported for shadow parity cannot observe it. Record it on
				// the shared ref and report it here, at the single point insertion begins.
				isRemixInsertingRef.current = true;
				editorRemixLifecycleAdapter?.observeLegacyPhase?.('inserting');
				shouldSettleRetainedLifecycleRef.current = true;
			}
			const settleSuccessfulInsertion = () => {
				if (!retainedLifecycle || !shouldSettleRetainedLifecycleRef.current) {
					return;
				}
				if (hasEarlyInsertedWhiteboardRef.current && hasDeferredWhiteboardRecoveryRef.current) {
					// The inserted early whiteboard remains recoverable. Keep the retained handle in
					// `inserting`; the token-bound recovery coordinator settles retry or discard.
					return;
				}
				if (!retainedLifecycle.transition({ type: 'INSERTED' })) {
					retainedLifecycle.rollback();
				}
			};

			const insertContent = async () => {
				if (isCancelled()) {
					return;
				}
				if (isUpdate && actionKey === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY) {
					// A whiteboard update streams into the already-selected board. Its completed
					// action is the acknowledgement for that existing artifact: record the new
					// invocation baseline without inserting, duplicating, or finalizing a board.
					if (
						!historyEntryForUpdate ||
						(!historyEntryForUpdate.url && !historyEntryForUpdate.fieldId)
					) {
						return;
					}
					return {
						acknowledgementTimedOut: false,
						fieldId: historyEntryForUpdate.fieldId,
						url: historyEntryForUpdate.url,
					};
				}
				if (actionKey === IMAGE_CREATE_KEY) {
					return handleInsertInfographicIntoPageRef.current();
				} else {
					const result = await handleInsertMauiEmbedIntoPageRef.current(isCancelled);
					if (isCancelled()) {
						return;
					}
					if (!result) {
						return;
					}

					if (hasEarlyInsertedWhiteboardRef.current) {
						// The ephemeral preview sends a final whiteboard bridge command with
						// isStreamComplete: true; the whiteboard client uses that final signal
						// for end-of-stream behaviours such as zoom-to-fit. Since the editor flow
						// inserts the whiteboard outside the preview, mirror that signal here once
						// streaming has completed, even when finalization fails and the inserted
						// diagram remains unfinalized.
						sendWhiteboardStreamCompleteCommand({
							data: finalWhiteboardCreateActionData,
							groupId: conversationId,
							singleInstrumentationID: agentMessageId,
						});
					}

					return result;
				}
			};

			if (
				!cancelledBeforeInsertion &&
				retainedLifecycle &&
				!hasEarlyInsertedWhiteboardRef.current
			) {
				onBeforeEditorRemixResultInsertion?.();
				const insertionStartAccepted = retainedLifecycle.transition({
					type: retainedLifecycle.hasAcceptedPreview() ? 'ACCEPTED' : 'DIRECT_INSERTION_STARTED',
				});
				if (!insertionStartAccepted) {
					shouldSettleRetainedLifecycleRef.current = false;
					retainedLifecycle.rollback();
					logLifecycleTelemetryBestEffort({
						operation: 'insertionStart',
						outcome: 'rejected',
						contentType: actionKey === IMAGE_CREATE_KEY ? 'image' : 'embed',
					});
				}
			}

			// `INSERTED` destroys the machine session, and with it the write-authority lease that the
			// completion callback's captured sink is bound to. Completion therefore has to run before
			// the settle, or the sink declines and nothing releases the AI entry points. Deferred with
			// a flag rather than moved into the `.then` below, because `.finally` also runs after a
			// failed insertion, where settling would send `INSERTED` on top of an errored flow.
			let hasInsertionSucceeded = false;
			insertContent()
				.then((result) => {
					if (isCancelled()) {
						return;
					}
					if (!result) {
						retainedLifecycle?.transition({ type: 'INSERTION_FAILED' });
						return;
					}
					const isRemixToolbarEnabled = fg('platform_editor_ai_remix_toolbar');
					if (result.acknowledgementTimedOut) {
						// The editor may have received this update, but its acknowledgement was
						// lost. Do not record a new displayed-artifact baseline or settle the
						// successful machine path; do release the original toolbar update so
						// bridge processors are not suppressed indefinitely.
						if (isUpdate && isRemixToolbarEnabled) {
							const completionId =
								historyEntryForUpdate?.fieldId ?? getMauiIdFromUrl(historyEntryForUpdate?.url);
							if (completionId) {
								onVariantUpdateComplete?.(completionId);
							}
						}
						return;
					}
					// The next update suppresses the acknowledged displayed artifact, never an
					// inherited history final message. Failed and cancelled attempts do not reach
					// this point, so they cannot replace the displayed artifact baseline.
					const admissionBaselineInvocationId = getRemixResultPayload([finalMessage])?.invocationId;
					const { fieldId, url } = result;
					if (!isUpdate) {
						const requestId = finalMessage.content.metadata?.request_id;
						onRemixInsertSuccess?.({
							currentConversationId: conversationId,
							requestId,
							remixMetadata: getRemixMetadata({
								action,
								selectedRemixMetadata: {
									selectedDynamicUiType: dynamicUiType,
									selectedDynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
									isObjectLevelRemix: !dynamicUiSource
										? undefined
										: isPageLevelRemix(dynamicUiSource),
									selectedContentAnatomy,
									selectedContentWordCount,
									unsupportedRemixNodeTypes,
									interactionSource,
								},
							}),
						});
					}
					if (!isRemixToolbarEnabled) {
						hasInsertionSucceeded = true;
						return;
					}
					if (hasEarlyInsertedWhiteboardRef.current && url) {
						finishRemixThinkingRef.current?.();
					}
					if (isUpdate) {
						if (conversationId && historyEntryForUpdate?.dynamicUiType) {
							addHistoryStackEntry?.({
								admissionBaselineInvocationId,
								conversationId,
								dynamicUiType: historyEntryForUpdate.dynamicUiType,
								// Preserve the backend-confirmed subtype for type-changing edits.
								dynamicUiSubtype: backendDynamicUiSubtype ?? dynamicUiSubtypeForUpdate,
								finalMessage,
								previousFieldId: historyEntryForUpdate.fieldId,
								previousUrl: historyEntryForUpdate.url,
								url,
								fieldId,
							});
						}
						// Release the in-flight mark so the bridge processors resume
						// handling BE pushes for this id. Must match the toolbar's
						// tracking key: image fieldId, or MAUI app id from the
						// *pre-update* stack-entry URL.
						const completionId =
							historyEntryForUpdate?.fieldId ?? getMauiIdFromUrl(historyEntryForUpdate?.url);
						if (completionId) {
							onVariantUpdateComplete?.(completionId);
						}
					} else {
						if (conversationId && dynamicUiSource && dynamicUiType) {
							addHistoryEntry?.({
								admissionBaselineInvocationId,
								conversationId,
								dynamicUiSource,
								// History and the variant picker must reflect the subtype the
								// backend produced, falling back to the user's pick if it returned none.
								dynamicUiSubtype: backendDynamicUiSubtype ?? dynamicUiSubtypeFromContext,
								dynamicUiType,
								finalMessage,
								contentClassification:
									contentClassification?.type === 'whiteboard'
										? { ...contentClassification, data: finalWhiteboardCreateActionData }
										: contentClassification,
								url,
								fieldId,
							});
						}
						// Via the host so the flag renders on a durable surface outliving this flow.
						if (isSubtypeMismatch) {
							onSubtypeMismatch?.();
						}
					}
					hasInsertionSucceeded = true;
				})
				.catch((error) => {
					if (isCancelled()) {
						return;
					}
					retainedLifecycle?.transition({ type: 'INSERTION_FAILED' });
					if (!retainedLifecycle) {
						throw error;
					}
				})
				.finally(() => {
					if (isCancelled()) {
						return;
					}
					resetContext();
					// Before the settle below: see `hasInsertionSucceeded`.
					setThinkingCompleteRef.current?.(true);
					if (hasInsertionSucceeded) {
						settleSuccessfulInsertion();
					}
				});
		}
	}, [
		isViewMode,
		action,
		actionKey,
		finalMessage,
		resetContext,
		conversationId,
		dynamicUiType,
		dynamicUiSource,
		selectedContentAnatomy,
		selectedContentWordCount,
		unsupportedRemixNodeTypes,
		interactionSource,
		dynamicUiSubtypeFromContext,
		backendDynamicUiSubtype,
		isSubtypeMismatch,
		onSubtypeMismatch,
		contentClassification,
		finalWhiteboardCreateActionData,
		agentMessageId,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onVariantUpdateComplete,
		isUpdate,
		historyEntryForUpdate?.dynamicUiType,
		dynamicUiSubtypeForUpdate,
		historyEntryForUpdate,
		retainedLifecycle,
		logLifecycleTelemetryBestEffort,
		editorRemixLifecycleAdapter,
		isRemixInsertingRef,
		onBeforeEditorRemixResultInsertion,
		hasMachineAuthority,
		isCancelled,
	]);
}
